// "Why it's faster than FTP", animated (one 12s loop, CSS keyframes on inline SVG — see AnimatedFigure).
// Two rows, the same photos at the same speed per file:
//  - FTP app: 2 lanes (2 at a time by default, and no more than 10), originals only; its gallery fills
//    slowly and goes live late.
//  - Web uploader: many lanes that widen and narrow to fit the connection ("adapts to your bandwidth"); light
//    previews go first, so its gallery goes live early, then the originals follow.
//  - The connection drops for a moment (everything holds), then the upload resumes by itself.
// Illustrative only: no figures. Reduced motion: frozen at 30% (web gallery live, FTP still filling).
// Gallery tiles are real running-event photos (./photos) once the figure nears the viewport: washed out with a
// gold edge while only the preview is up, full colour with an orange edge once the original has landed.
import type { CSSProperties } from "react";
import { AnimatedFigure } from "./AnimatedFigure";
import { useFigureNear } from "./figureNear";
import { runPhoto } from "./photos";
import { LINEAR, kf, tr, type Stop } from "./keyframes";
import type { WebUploaderCopy } from "@/content/webUploader";

const SCOPE = "wuftp";
const DUR = 12;
const FREEZE = 30;

// ---- geometry (viewBox 400 × 304) -------------------------------------------------------------
const LANE_X = 14;
const LANE_W = 284;
const FTP_LANE_Y = [50, 66];
const WEB_LANE_Y = Array.from({ length: 8 }, (_, k) => 156 + k * 14);
const LANE_H = 9;
const FILE = { preview: 10, original: 24 };
const GAL_FTP = { x: 310, y: 40 };
const GAL_WEB = { x: 310, y: 166 };
const GAL_TILE = (g: { x: number; y: number }, i: number) => ({ x: g.x + 7 + (i % 4) * 17, y: g.y + 20 + Math.floor(i / 4) * 13 });

// ---- timeline (% of the loop) -----------------------------------------------------------------
const DROP = [58, 64] as const;
const RESUMED_UNTIL = 71;
const LAST_END = 94;
const PREVIEWS_UNTIL = 16; // a trip starting before this is a preview
const LEN = { preview: 6, original: 20 }; // light previews are small, so each one is quick
/** A little stagger per web lane, so the lanes don't move in lockstep. */
const WEB_OFFSET = [0, 1.5, 0.7, 2.2, 0, 1.1, 0.4, 1.8];
/** When each web lane is open: four always, two more early on, the last two narrow away for a while. */
const WEB_WINDOWS: [number, number][][] = [
  [[0, 100]], [[0, 100]], [[0, 100]], [[0, 100]],
  [[8, 100]], [[8, 100]],
  [[14, 44], [52, 100]], [[14, 44], [52, 100]],
];

interface Trip { lane: number; start: number; end: number; kind: "preview" | "original" }

/** End time of a trip of active length `len` starting at `start`: a trip under way when the connection
 *  drops holds for the drop. Starts inside the drop wait for it to end. */
const plan = (start: number, len: number) => {
  const s = start >= DROP[0] && start < DROP[1] ? DROP[1] : start;
  const e = s + len;
  return { start: s, end: s < DROP[0] && e > DROP[0] ? e + (DROP[1] - DROP[0]) : e };
};

const trips = (windows: [number, number][], lane: number, previews: boolean, offset = 0): Trip[] => {
  const out: Trip[] = [];
  for (const [from, to] of windows) {
    let t = from + offset;
    for (;;) {
      const kind = previews && t < PREVIEWS_UNTIL ? "preview" : "original";
      const p = plan(t, LEN[kind]);
      if (p.end > Math.min(to, LAST_END)) break;
      out.push({ lane, kind, ...p });
      t = p.end + 1;
    }
  }
  return out;
};

const FTP_TRIPS = [...trips([[0, 100]], 0, false), ...trips([[0, 100]], 1, false, 10)];
const WEB_TRIPS = WEB_WINDOWS.flatMap((w, lane) => trips(w, lane, true, WEB_OFFSET[lane]));
const firstEnds = (ts: Trip[], n: number) => ts.map((t) => t.end).sort((a, b) => a - b).slice(0, n);
const FTP_FILLS = firstEnds(FTP_TRIPS, 8);
const WEB_FILLS = firstEnds(WEB_TRIPS.filter((t) => t.kind === "preview"), 8);
/** The web gallery's tiles turn from preview to original as the first originals land. */
const WEB_ORIGINALS = firstEnds(WEB_TRIPS.filter((t) => t.kind === "original"), 8);
const FTP_LIVE = FTP_FILLS[FTP_FILLS.length - 1];
const WEB_LIVE = WEB_FILLS[WEB_FILLS.length - 1] + 1;

const name = (el: string) => `${SCOPE}-${el}`;
const anim = (el: string): CSSProperties => ({ animationName: name(el) });

const tripKeyframes = (id: string, t: Trip, y: number): string => {
  const w = FILE[t.kind];
  const x0 = LANE_X, x1 = LANE_X + LANE_W - w;
  const stops: Stop[] = [
    [0, `${tr(x0, y)};opacity:0`],
    [t.start - 0.01, `${tr(x0, y)};opacity:0`],
    [t.start, `${tr(x0, y)};opacity:1;${LINEAR}`],
  ];
  if (t.start < DROP[0] && t.end > DROP[0]) {
    const x = x0 + ((x1 - x0) * (DROP[0] - t.start)) / LEN[t.kind]; // where it holds while the connection is down
    stops.push([DROP[0], `${tr(x, y)};opacity:1`], [DROP[1], `${tr(x, y)};opacity:1`]);
  }
  stops.push([t.end, `${tr(x1, y)};opacity:1`], [t.end + 0.6, `${tr(x1, y)};opacity:0`], [100, `${tr(x1, y)};opacity:0`]);
  return kf(name(id), stops);
};

const show = (el: string, from: number, to = 96, fade = 0.8) =>
  kf(name(el), [[0, "opacity:0"], [from - fade, "opacity:0"], [from, "opacity:1"], [to, "opacity:1"], [Math.min(100, to + fade), "opacity:0"], [100, "opacity:0"]]);

function buildKeyframes(): string {
  const out: string[] = [];
  FTP_TRIPS.forEach((t, i) => out.push(tripKeyframes(`f${i}`, t, FTP_LANE_Y[t.lane])));
  WEB_TRIPS.forEach((t, i) => out.push(tripKeyframes(`w${i}`, t, WEB_LANE_Y[t.lane] + (t.kind === "preview" ? 1.5 : 0))));
  FTP_FILLS.forEach((p, i) => out.push(show(`ft${i}`, p + 0.6)));
  WEB_FILLS.forEach((p, i) => out.push(show(`wt${i}`, p + 0.6)));
  WEB_ORIGINALS.forEach((p, i) => out.push(show(`wo${i}`, p + 0.6)));
  out.push(show("ftplive", FTP_LIVE + 1), show("weblive", WEB_LIVE));
  // web lanes open and close; the bandwidth bar follows how many are open
  WEB_WINDOWS.forEach((ws, lane) => {
    if (ws[0][0] === 0 && ws.length === 1) return;
    const stops: Stop[] = [[0, "opacity:.2"]];
    for (const [a, b] of ws) {
      stops.push([a - 1, "opacity:.2"], [a, "opacity:1"]);
      if (b < 100) stops.push([b + 0.5, "opacity:1"], [b + 2, "opacity:.2"]);
    }
    stops.push([97, "opacity:1"], [99.5, "opacity:.2"], [100, "opacity:.2"]);
    out.push(kf(name(`lane${lane}`), stops));
  });
  const open = (p: number) => WEB_WINDOWS.filter((ws) => ws.some(([a, b]) => p >= a && p < b)).length;
  const barStops: Stop[] = [];
  for (const p of [0, 7.9, 8, 13.9, 14, 43.9, 45.5, 51.9, 52, 97, 99.5, 100]) {
    const n = p >= 99.5 ? 4 : open(p);
    barStops.push([p, `transform:scaleY(${(n * 14 - 5) / (8 * 14 - 5)})`]);
  }
  out.push(kf(name("bar"), barStops));
  // the drop
  out.push(kf(name("hold"), [[0, "opacity:1"], [DROP[0], "opacity:1"], [DROP[0] + 0.6, "opacity:.45"], [DROP[1] - 0.4, "opacity:.45"], [DROP[1], "opacity:1"], [100, "opacity:1"]]));
  out.push(show("dropped", DROP[0] + 0.3, DROP[1] - 0.2, 0.5), show("resumed", DROP[1], RESUMED_UNTIL, 0.6));
  return out.join("");
}

const KEYFRAMES = buildKeyframes();

/** One gallery tile at the origin: a preview (washed out, gold edge) or an original (orange edge); a plain
 *  colour block until the photos load. */
const GalleryPhoto = ({ i, preview }: { i: number; preview?: boolean }) => {
  const near = useFigureNear();
  return (
    <>
      <rect width={14} height={10} rx={2} className={preview ? "fill-hawk-gold" : "fill-hawk-orange"} />
      {near && (
        <>
          <image href={runPhoto(i)} width={14} height={10} preserveAspectRatio="xMidYMid slice" clipPath={`url(#${SCOPE}-clip)`} />
          {preview && <rect width={14} height={10} rx={2} fill="#fff" opacity={0.45} />}
          <rect x={0.5} y={0.5} width={13} height={9} rx={1.5} fill="none" className={preview ? "stroke-hawk-gold" : "stroke-hawk-orange"} strokeWidth={1} />
        </>
      )}
    </>
  );
};

const Gallery = ({ g, id, n, label, preview, upgrade }: { g: { x: number; y: number }; id: string; n: number; label: string; preview?: boolean; upgrade?: string }) => (
  <g>
    <rect x={g.x} y={g.y} width={80} height={56} rx={8} className="fill-background stroke-border" />
    <text x={g.x + 7} y={g.y + 13} className="fill-muted-foreground" fontSize={9} fontWeight={600}>{label}</text>
    {Array.from({ length: 8 }, (_, i) => {
      const p = GAL_TILE(g, i);
      return <rect key={i} x={p.x} y={p.y} width={14} height={10} rx={2} fill="none" className="stroke-border" strokeDasharray="2 2" />;
    })}
    {Array.from({ length: n }, (_, i) => {
      const p = GAL_TILE(g, i);
      return (
        <g key={i} data-a="" style={anim(`${id}${i}`)} transform={`translate(${p.x} ${p.y})`}>
          <GalleryPhoto i={i} preview={preview} />
        </g>
      );
    })}
    {upgrade &&
      Array.from({ length: n }, (_, i) => {
        const p = GAL_TILE(g, i);
        return (
          <g key={i} data-a="" style={anim(`${upgrade}${i}`)} transform={`translate(${p.x} ${p.y})`}>
            <GalleryPhoto i={i} />
          </g>
        );
      })}
  </g>
);

const LivePill = ({ g, id, text }: { g: { x: number; y: number }; id: string; text: string }) => (
  <g data-a="" style={anim(id)}>
    <rect x={g.x + 12} y={g.y + 62} width={56} height={16} rx={8} fill="#059669" />
    <text x={g.x + 40} y={g.y + 73.5} textAnchor="middle" fill="#fff" fontSize={9.5} fontWeight={700}>{text}</text>
  </g>
);

const File = ({ id, kind }: { id: string; kind: Trip["kind"] }) => (
  <rect data-a="" style={anim(id)} width={FILE[kind]} height={kind === "preview" ? LANE_H - 3 : LANE_H} rx={2}
    className={kind === "preview" ? "fill-hawk-gold" : "fill-hawk-orange"} opacity={kind === "preview" ? 0.75 : 1} />
);

export const FasterThanFtpAnimation = ({ copy, pause, play }: { copy: WebUploaderCopy["faster"]; pause: string; play: string }) => {
  const a = copy.anim;
  return (
    <AnimatedFigure
      scope={SCOPE}
      durationS={DUR}
      freezeAt={FREEZE}
      keyframes={KEYFRAMES}
      label={a.label}
      pauseLabel={pause}
      playLabel={play}
      description={
        <>
          <span className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-foreground">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-2 w-3 rounded-sm bg-hawk-gold opacity-75" aria-hidden="true" /> {a.legendPreviews}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-6 rounded-sm bg-hawk-orange" aria-hidden="true" /> {a.legendOriginals}
            </span>
          </span>
          {a.description}
        </>
      }
    >
      <div className="overflow-hidden rounded-2xl border border-border bg-card p-3 shadow-lg sm:p-4">
        <svg viewBox="0 0 400 304" className="block h-auto w-full" role="presentation" focusable="false">
          <defs>
            <clipPath id={`${SCOPE}-clip`}>
              <rect width={14} height={10} rx={2} />
            </clipPath>
          </defs>
          {/* FTP app */}
          <text x={LANE_X} y={20} className="fill-foreground" fontSize={13} fontWeight={700}>{a.ftp}</text>
          <text x={LANE_X} y={36} className="fill-muted-foreground" fontSize={10.5}>{`${a.ftpNote} · ${a.ftpMax}`}</text>
          <g data-a="" style={anim("hold")}>
            {FTP_LANE_Y.map((y) => <rect key={y} x={LANE_X} y={y} width={LANE_W} height={LANE_H} rx={4.5} className="fill-muted" />)}
            {FTP_TRIPS.map((t, i) => <File key={i} id={`f${i}`} kind={t.kind} />)}
          </g>
          <Gallery g={GAL_FTP} id="ft" n={FTP_FILLS.length} label={a.gallery} />
          <LivePill g={GAL_FTP} id="ftplive" text={a.live} />

          <line x1={0} y1={112} x2={400} y2={112} className="stroke-border" />

          {/* Web uploader */}
          <text x={LANE_X} y={134} className="fill-foreground" fontSize={13} fontWeight={700}>{a.web}</text>
          <text x={LANE_X} y={148} className="fill-muted-foreground" fontSize={10.5}>{a.webNote}</text>
          <rect data-a="" style={{ ...anim("bar"), transformBox: "fill-box", transformOrigin: "top" }} x={4} y={WEB_LANE_Y[0]} width={3} height={8 * 14 - 5} rx={1.5} className="fill-hawk-orange" />
          <g data-a="" style={anim("hold")}>
            {WEB_LANE_Y.map((y, lane) => (
              <rect key={y} data-a="" style={WEB_WINDOWS[lane].length === 1 && WEB_WINDOWS[lane][0][0] === 0 ? undefined : anim(`lane${lane}`)}
                x={LANE_X} y={y} width={LANE_W} height={LANE_H} rx={4.5} className="fill-muted" />
            ))}
            {WEB_TRIPS.map((t, i) => <File key={i} id={`w${i}`} kind={t.kind} />)}
          </g>
          <Gallery g={GAL_WEB} id="wt" n={WEB_FILLS.length} label={a.gallery} preview upgrade="wo" />
          <LivePill g={GAL_WEB} id="weblive" text={a.live} />
          <text x={LANE_X} y={286} className="fill-hawk-orange" fontSize={11} fontWeight={600}>↕ {a.adapts}</text>

          {/* The connection drops, then resumes by itself */}
          <g data-a="" style={anim("dropped")}>
            <rect x={56} y={82} width={240} height={26} rx={13} fill="#fff1f2" stroke="#e11d48" />
            <text x={176} y={99} textAnchor="middle" fill="#be123c" fontSize={12} fontWeight={700}>{a.dropped}</text>
          </g>
          <g data-a="" style={anim("resumed")}>
            <rect x={56} y={82} width={240} height={26} rx={13} fill="#ecfdf5" stroke="#059669" />
            <text x={176} y={99} textAnchor="middle" fill="#047857" fontSize={12} fontWeight={700}>{a.resumed}</text>
          </g>
        </svg>
      </div>
    </AnimatedFigure>
  );
};
